import { getFirebaseMessaging, getToken, isSupported, onMessage, saveFcmToken, saveInstalledAppVersion } from './firebase';
import { isNativeApp } from '../utils/platform';
import { getInstalledAppVersion } from '../utils/requiredUpdate';

let messagingInstance = null;
let nativeListenersBound = false;
// The uid the current device token belongs to. The native 'registration'
// listener is bound once but must always save under the *current* driver — not
// whoever was logged in when it was first bound (account switch on one device).
let currentPushUid = null;

const ensureWebMessaging = async () => {
  if (!(await isSupported())) return null;
  if (!messagingInstance) messagingInstance = getFirebaseMessaging();
  return messagingInstance;
};

const initNativePush = async (uid, { request = true } = {}) => {
  currentPushUid = uid;
  const { PushNotifications } = await import('@capacitor/push-notifications');

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive !== 'granted') {
    if (!request || perm.receive === 'denied') return null;
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return null;

  // Android 8+: the notification CHANNEL owns the sound (and is frozen once
  // created) — the FCM payload's `sound` field is ignored. Do NOT pass `sound`
  // here: Capacitor treats the value as a res/raw/<name> filename, there is no
  // such file, and the channel silently ends up with no tone. Omitting `sound`
  // makes the channel use the device's default notification tone. Fresh `_v3`
  // ids because the older channels are already frozen-silent on installed
  // devices and cannot be repaired.
  try {
    await PushNotifications.createChannel({
      id: 'ride_requests_v3',
      name: 'Ride requests',
      description: 'New ride alerts while you are online',
      importance: 5,
      visibility: 1,
      vibration: true,
      lights: true,
    });
  } catch { /* channel API unavailable on very old Android */ }

  try {
    await PushNotifications.createChannel({
      id: 'ride_alerts_v3',
      name: 'Ride updates',
      description: 'Assignment, cancellation, and trip alerts',
      importance: 5,
      visibility: 1,
      vibration: true,
      lights: true,
    });
  } catch { /* */ }

  // Retire the earlier channels — created with a broken sound URI (see above),
  // and channel settings can't be changed after creation. Removing them clears
  // the dead, silent entries from the Anna's notification settings.
  try { await PushNotifications.deleteChannel({ id: 'ride_requests_v2' }); } catch { /* */ }
  try { await PushNotifications.deleteChannel({ id: 'ride_requests' }); } catch { /* */ }

  if (!nativeListenersBound) {
    nativeListenersBound = true;
    await PushNotifications.addListener('registration', async ({ value }) => {
      if (!value || !currentPushUid) return;
      await saveFcmToken(currentPushUid, value);
      // Fire-and-forget: dispatch.js only needs this to decide the payload
      // shape for future ride offers, so it must never delay or block token
      // registration itself.
      getInstalledAppVersion()
        .then((version) => { if (version) saveInstalledAppVersion(currentPushUid, version); })
        .catch(() => {});
    });
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('FCM registration error:', err);
    });
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      window.dispatchEvent(new CustomEvent('mt_driver_push', { detail: notification }));
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action?.notification?.data || {};
      window.dispatchEvent(new CustomEvent('mt_push_navigate', { detail: data }));
    });
  }

  await PushNotifications.register();
  return 'native';
};

const initWebPush = async (uid, { request = true } = {}) => {
  currentPushUid = uid;
  const vapid = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!vapid) {
    console.warn('Missing REACT_APP_FIREBASE_VAPID_KEY — push disabled');
    return null;
  }
  try {
    const messaging = await ensureWebMessaging();
    if (!messaging) return null;

    if (typeof Notification === 'undefined') return null;
    if (Notification.permission === 'denied') return null;
    if (Notification.permission !== 'granted') {
      if (!request) return null;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return null;
    }

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(messaging, {
      vapidKey: vapid,
      serviceWorkerRegistration: registration,
    });
    if (token) await saveFcmToken(uid, token);
    return token;
  } catch (e) {
    console.error('FCM init failed:', e);
    return null;
  }
};

/**
 * Register for ride-request push.
 * @param {string} uid driver uid
 * @param {{ request?: boolean }} opts if request=false, only refreshes token when already granted
 */
export const initDriverMessaging = async (uid, opts = {}) => {
  if (!uid || typeof window === 'undefined') return null;
  const request = opts.request !== false;
  if (isNativeApp()) return initNativePush(uid, { request });
  return initWebPush(uid, { request });
};

/**
 * On logout / account switch: stop this device's token being attributed to `uid`
 * and delete it from that driver's doc so ride requests stop being delivered here.
 */
export const clearDriverMessaging = async (uid) => {
  currentPushUid = null;
  try {
    const { clearFcmToken } = await import('./firebase');
    await clearFcmToken(uid);
  } catch (e) {
    console.warn('clearDriverMessaging: token doc cleanup failed:', e?.message);
  }
  try {
    if (!isNativeApp()) {
      const messaging = await ensureWebMessaging();
      if (messaging) {
        const { deleteToken } = await import('firebase/messaging');
        await deleteToken(messaging).catch(() => {});
      }
    }
  } catch (e) {
    console.warn('clearDriverMessaging: token invalidation failed:', e?.message);
  }
};

/**
 * Clear a ride's "New ride request" notification from the tray (best effort).
 * Used when the ride is taken / cancelled / expires while the app is alive; a
 * killed app is handled natively by RideMessagingService instead.
 */
export const clearRideNotification = async (rideId) => {
  if (!rideId || !isNativeApp()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const { notifications } = await PushNotifications.getDeliveredNotifications();
    const match = (notifications || []).filter((n) => {
      const d = n?.data || {};
      return d.rideId === rideId || n?.tag === `ride:${rideId}`;
    });
    if (match.length) await PushNotifications.removeDeliveredNotifications({ notifications: match });
  } catch { /* plugin unavailable / nothing to clear */ }
};

export const listenForegroundMessages = (handler) => {
  if (typeof window === 'undefined') return () => {};

  const onNative = (e) => {
    const n = e.detail || {};
    handler({
      notification: {
        title: n.title || n.notification?.title,
        body: n.body || n.notification?.body,
      },
      data: n.data || {},
    });
  };
  window.addEventListener('mt_driver_push', onNative);

  let unsubWeb = () => {};
  (async () => {
    try {
      if (isNativeApp()) return;
      const messaging = await ensureWebMessaging();
      if (!messaging) return;
      unsubWeb = onMessage(messaging, handler);
    } catch { /* */ }
  })();

  return () => {
    window.removeEventListener('mt_driver_push', onNative);
    unsubWeb();
  };
};

/** Open dashboard (or trip) when a notification is tapped. */
export const handlePushDeepLink = (navigate) => {
  if (typeof window === 'undefined' || typeof navigate !== 'function') return () => {};
  const handler = (e) => {
    const type = e.detail?.type;
    const rideId = e.detail?.rideId;
    if (type === 'chat' && rideId) {
      navigate(`/trip/${rideId}`);
      return;
    }
    // New ride requests land on Home so the accept card can show.
    navigate('/');
  };
  window.addEventListener('mt_push_navigate', handler);
  return () => window.removeEventListener('mt_push_navigate', handler);
};
